import TurnDownServices from "turndown";

export const formatDate = (date) => {
    return date.toUTCString(); // Outputs the date in UTC format
}

export const convertToMarkdown = (html) => {
    const turndownService = new TurnDownServices();

    // Rule for underlined text
    turndownService.addRule('underline', {
        filter: ['u'],
        replacement: function (content) {
            return `__${content}__`;
        }
    });

    // Rule for unordered lists
    turndownService.addRule("quillUnorderedList", {
        filter: (node) =>
            node.nodeName === "LI" && node.getAttribute("data-list") === "bullet",
        replacement: (content) => {
            content = content.replace(/^\s*$\n?/gm, '').trim();
            return `- ${content}\n`;
        }
    });

    // Rule for ordered lists
    turndownService.addRule("quillOrderedList", {
        filter: (node) =>
            node.nodeName === "LI" && node.getAttribute("data-list") === "ordered",
        replacement: function (content, node) {
            // Get all ordered list items that come before this one
            const allItems = Array.from(node.parentNode.children);
            const orderedItemsBeforeThis = allItems
                .slice(0, allItems.indexOf(node))
                .filter(item => item.getAttribute("data-list") === "ordered");

            // Calculate the correct index based only on ordered items
            const index = orderedItemsBeforeThis?.length + 1;

            // Clean up the content
            content = content.replace(/^\s*$\n?/gm, '').trim();

            return `${index}. ${content}\n`;
        }
    });

    // Rule for strikethrough
    turndownService.addRule('strikethrough', {
        filter: ['s', 'strike', 'del'],
        replacement: function (content) {
            return `~~${content}~~`;
        }
    });

    // Remove empty Quill UI spans
    turndownService.addRule("removeQuillUI", {
        filter: (node) =>
            node.classList && node.classList.contains('ql-ui'),
        replacement: () => ''
    });

    // Rule for mentions
    turndownService.addRule('quillMention', {
        filter: function (node) {
            return (
                node.nodeName === 'SPAN' &&
                node.classList &&
                node.classList.contains('mention') &&
                node.getAttribute('data-id')
            );
        },
       replacement: function (content, node) {
           const id = node.getAttribute('data-id');
           let value = node.textContent || '';
           value = value.trim().replace(/^@/,'');
           return `[${value}](/community/profiles/${id})`;
       }
    });

    let markdown = turndownService.turndown(html);
    // Normalize newlines: replace Windows-style CRLF and CR with LF
    // markdown = markdown.replace(/\r\n|\r/g, '\n');
    // Remove lines that are only spaces
    markdown = markdown.replace(/\n{2,}/g, '\r\n');
    return markdown;
}

export const convertMarkdownToHtml = (markdown) => {
    if (!markdown) return '';
    // Remove all leading empty lines and whitespace at the start
    markdown = markdown.replace(/^[\s\r\n]+/, '');
    const style = 'background-color: rgb(255, 255, 255); color: rgb(0, 0, 0);';
    const qlUiSpan = '<span class="ql-ui" contenteditable="false"></span>';

    // Normalize line endings to \n
    markdown = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Clean over-escaped characters in URLs
    markdown = markdown.replace(/\\\[/g, '[').replace(/\\\]/g, ']');
    // Clean escaped asterisks that shouldn't be escaped
    markdown = markdown.replace(/\\\*/g, '*');
    markdown = markdown.replace(/\\\\+/g, '\\');

   // Mention markdown to HTML span
   let html = markdown.replace(/\[([^\]]+)\]\(\/community\/profiles\/(\d+)\)/g, (match, value, id) => {
       return `<span class="mention" data-index="0" data-denotation-char="@" data-id="${id}" data-value="${value.replace(/^@/, '')}"><span contenteditable="false"><span class="ql-mention-denotation-char">@</span><span class="ql-mention-value">${value.replace(/^@/, '')}</span></span></span>`;
   });
    console.log('Converting mentions in HTML:', html);

    // converting markdown links to HTML and temporarily replace them with placeholders
    let linkPlaceholders = [];
    let linkIndex = 0;
    // html = markdown.replace(/__\[(.*?)\]\((.*?)\)__/g, (match, text, url) => {
    //     const tag = `<u style="${style}"><a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a></u>`;
    //     linkPlaceholders.push(tag);
    //     return `<<LINKPLACEHOLDER${linkIndex++}>>`;
    // });
    html = html.replace(/\[([^\]]+)\]\((https:\/\/[^\)]+)\)/g, (match, text, url) => {
        const tag = `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        linkPlaceholders.push(tag);
        return `<<LINKPLACEHOLDER${linkIndex++}>>`;
    });

    // Applying formatting replacements only to non-link text
    // Combined formatting
    html = html.replace(/\*\*__([^*]+?)__\*\*/g, `<strong style="${style}"><u>$1</u></strong>`);
    html = html.replace(/\*__([^*]+?)__\*/g, `<em style="${style}"><u>$1</u></em>`);
    html = html.replace(/__\*\*([^_]+?)\*\*__/g, `<u><strong style="${style}">$1</strong></u>`);
    html = html.replace(/__\*([^_]+?)\*__/g, `<u><em style="${style}">$1</em></u>`);

    // Individual styles
    html = html.replace(/__([^_]+?)__/g, `<u style="${style}">$1</u>`);
    html = html.replace(/\*\*([^*]+?)\*\*/g, `<strong style="${style}">$1</strong>`);
    html = html.replace(/_([^_]+?)_/g, `<em style="${style}">$1</em>`);

    // Strikethrough
    html = html.replace(/~~([^~]+?)~~/g, `<s style="${style}">$1</s>`);

    // Restore link placeholders
    html = html.replace(/<<LINKPLACEHOLDER(\d+)>>/g, (_, idx) => linkPlaceholders[Number(idx)]);

    // Unordered list items
    html = html.replace(/^-\s+(.*)$/gm, (_, itemText) =>
        `<li data-list="bullet">${qlUiSpan}<span style="${style}">${itemText}</span></li>`
    );

    // Ordered list items
    html = html.replace(/^\d+\.\s+(.*)$/gm, (_, itemText) =>
        `<li data-list="ordered">${qlUiSpan}<span style="${style}">${itemText}</span></li>`
    );

    // Wrap bullet list blocks in <ol>
    html = html.replace(/((<li data-list="bullet">[\s\S]*?<\/li>\s*)+)/g, match => {
        return `<ol>${match.trim().replace(/\n/g, '')}</ol>`;
    });

    // Wrap ordered list blocks in <ol>
    html = html.replace(/((<li data-list="ordered">[\s\S]*?<\/li>\s*)+)/g, match => {
        return `<ol>${match.trim().replace(/\n/g, '')}</ol>`;
    });

    // Split by single newlines and wrap each line in a paragraph
    // This preserves the line break structure from your markdown
    const lines = html.split('\n');
    
    html = lines.map(line => {
        // Don't wrap list items in paragraphs (they're already wrapped in <ol>)
        if (line.trim().startsWith('<ol>') || line.trim().startsWith('</ol>')) {
            return line;
        }
        if (line.trim() === '') {
            return `<br>`;
        }
        return `<p><span style="${style}">${line}</span></p>`;
    }).join('');

    // Remove empty <p> tags
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
};


export const getFormattedDate = (date) => {
    const today = new Date();
    const year = date.getFullYear();
    const isCurrentYear = year === today.getFullYear();

    const options = { month: "long", day: "2-digit" };
    if (!isCurrentYear) {
        options.year = "numeric";
    }

    return date.toLocaleDateString("en-US", options);
}

// Function to calculate relative time
export const getRelativeTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    let interval = Math.floor(seconds / 31536000); // Years
    if (interval > 1) return `${interval} years ago`;
    if (interval === 1) return `1 year ago`;

    interval = Math.floor(seconds / 2592000); // Months
    if (interval > 1) return `${interval} months ago`;
    if (interval === 1) return `1 month ago`;

    interval = Math.floor(seconds / 86400); // Days
    if (interval > 1) return `${interval} days ago`;
    if (interval === 1) return `1 day ago`;

    interval = Math.floor(seconds / 3600); // Hours
    if (interval > 1) return `${interval} hours ago`;
    if (interval === 1) return `1 hour ago`;

    interval = Math.floor(seconds / 60); // Minutes
    if (interval > 1) return `${interval} minutes ago`;
    if (interval === 1) return `1 minute ago`;

    return seconds <= 5 ? "just now" : `${seconds} seconds ago`;
};


export const extractFirstLink = (content) => {
    // Match both markdown links and plain URLs
    const match = content?.match(/(?:\[.*?\]\((https?:\/\/[^\s\)]+)\))|((https?:\/\/|www\.)[-\w@:%.\+~#=]{1,256}\.[a-z0-9()]{1,6}\b([-\w()@:%\+.~#?&//=]*))/i);

    return match ? match[1] || match[2] : null;
}

/**
 * Export the mention configuration for Quill editor - reusable function
 * @param currentMentionListRef
 * @param fetchUsers
 * @param lastEmptySearchRef
 */
export function getMentionConfig({ currentMentionListRef, fetchUsers, lastEmptySearchRef }) {
    return {
        allowedChars: /^[A-Za-z0-9\sÅÄÖåäö]*$/,
        mentionDenotationChars: ["@"],
        spaceAfterInsert: true,
        isolateCharacter: true,
        source: async function (searchTerm, renderList) {
            if (/^\s/.test(searchTerm)) {
                renderList([]);
                return;
            }
            if (lastEmptySearchRef.current && searchTerm.startsWith(lastEmptySearchRef.current)) {
                renderList([]);
                return;
            }
            fetchUsers(searchTerm, (results) => {
                currentMentionListRef.current = results;
                if (results.length === 0) {
                    lastEmptySearchRef.current = searchTerm;
                } else {
                    lastEmptySearchRef.current = "";
                }
                renderList(results);
            });
        },
        renderItem: function(item) {
            const container = document.createElement("div");
            container.className = "ql-mention-list-item-with-avatar";
            const img = document.createElement("img");
            img.src = item.avatar;
            img.alt = item.value;
            img.className = "ql-mention-avatar";
            const span = document.createElement("span");
            span.innerText = item.value;
            container.appendChild(img);
            container.appendChild(span);
            return container;
        },
        renderLoading: () => {
            const skeleton = document.createElement("div");
            skeleton.className = "mention-loading-skeleton";
            skeleton.style.display = "flex";
            skeleton.style.alignItems = "center";
            skeleton.style.gap = "8px";
            skeleton.innerHTML = `
        <span class="skeleton-avatar" style="width:24px;height:24px;border-radius:50%;background:#eee;display:inline-block;"></span>
        <span class="skeleton-line" style="width:80px;height:12px;border-radius:4px;background:#eee;display:inline-block;"></span>
      `;
            return skeleton;
        },
    };
}